import { useCallback, useEffect, useRef } from "react";
import TspApiService from "../../../api/tspApiService";
import useTspApiResource, { tspErrorMessage } from "../../../hooks/useTspApiResource";
import { buildVisitUpdatePayload, visitToDraft } from "../../city-visits/cityVisitsModel";
import { uploadMedia, removeMedia as deleteMedia } from "../../media-gallery/useTspMediaData";
import { createAdvice, updateAdvice as saveAdviceChanges, removeAdvice as deleteAdvice } from "../../advice-repository/useTspAdviceData";
import { buildUpdateBody } from "../../advice-repository/adviceModel";
import { ADVICE_SECTIONS } from "../../../constants/tspAdvice";
import { accommodationBody, adviceBody, autoVisitBody, bookBatchBody, responseBody, visitBody } from "../addJourneyCityDetails";

const NOTHING = () => Promise.resolve({ data: null });

// The place store of an EXISTING journey (Edit Journey): the same interface as
// stagedPlaceStore, but every record is read from and saved through the API
// the rest of the app uses (city visits, book distribution, accommodation,
// media, advice, public response) — so every server rule and permission applies.
//
// `visitRecord` is the place's recorded visit (from the page's one visits list)
// or null; `load` = { books, stays, media, advice, responses } — a section's
// records are fetched the first time its tab is opened, not for every card at
// once. `onVisitChanged()` asks the page to re-read the journey and its visits
// (a visit changes the route's visit state). `range` = the journey's dates.
// `onSaved()` (optional) is told after every successful change — e.g. so a page
// can refresh the totals it shows.
//
// Book distribution, media and public response are filed under the place's
// visit. Their forms never wait for Visit Info: saving one for a place without
// a visit records the visit first, on the place's visit date (ensureVisit).
export default function useLivePlaceStore({ journeyId, place, visitRecord, load, perms, range, onVisitChanged, onSaved }) {
  const visitId = visitRecord ? visitRecord.id : null;
  const locationId = place.locationId;

  // The visit being recorded automatically (one request, however many saves
  // arrive meanwhile); forgotten once the page has the visit itself.
  const pendingVisit = useRef(null);
  useEffect(() => {
    if (visitId) pendingVisit.current = null;
  }, [visitId]);
  const ensureVisit = async () => {
    if (visitId) return visitId;
    if (!pendingVisit.current) {
      const date = place.plannedDate || range.startDate || new Date().toISOString().slice(0, 10);
      pendingVisit.current = TspApiService.createCityVisit(autoVisitBody(journeyId, locationId, date))
        .then((response) => response.data.id)
        .catch((error) => {
          // Recorded meanwhile (another tab, or a reply that was lost): use that visit.
          const details = error?.response?.data?.details;
          if (details?.code === "CITY_ALREADY_VISITED" && details.visit_id) return details.visit_id;
          throw error;
        });
    }
    try {
      return await pendingVisit.current;
    } catch (error) {
      pendingVisit.current = null;
      throw error;
    }
  };
  // After a save that may have recorded the visit: re-read the list, and the
  // journey + visits when the visit is new.
  const afterVisitSave = (reload) => () => {
    reload();
    if (!visitId) onVisitChanged();
  };

  const books = useTspApiResource(
    () => (load.books && visitId && perms.booksView ? TspApiService.listBookDistributions({ city_visit_id: visitId, limit: 100 }) : NOTHING()),
    [load.books, visitId, perms.booksView]
  );
  const stays = useTspApiResource(
    () => (load.stays && locationId && perms.staysView ? TspApiService.accommodations.list({ journey_id: journeyId, location_id: locationId, limit: 100 }) : NOTHING()),
    [load.stays, journeyId, locationId, perms.staysView]
  );
  const media = useTspApiResource(
    () => (load.media && locationId && perms.mediaView ? TspApiService.media.list({ journey_id: journeyId, location_id: locationId, limit: 50 }) : NOTHING()),
    [load.media, journeyId, locationId, perms.mediaView]
  );
  const advice = useTspApiResource(
    () => (load.advice && locationId && perms.adviceView ? TspApiService.advice.list({ journey_id: journeyId, location_id: locationId, limit: 20 }) : NOTHING()),
    [load.advice, journeyId, locationId, perms.adviceView]
  );
  const responses = useTspApiResource(
    () => (load.responses && visitId && perms.responsesView ? TspApiService.listPublicResponses({ journey_id: journeyId, city_visit_id: visitId, limit: 100 }) : NOTHING()),
    [load.responses, journeyId, visitId, perms.responsesView]
  );

  // Runs one API change, then re-reads what it touched. -> { ok } | { ok: false, message }
  const savedRef = useRef(onSaved);
  savedRef.current = onSaved;
  const act = useCallback(async (call, reload, fallback) => {
    try {
      await call();
      if (reload) reload();
      if (savedRef.current) savedRef.current();
      return { ok: true };
    } catch (error) {
      return { ok: false, message: tspErrorMessage(error, fallback) };
    }
  }, []);

  const saveVisit = (draft) =>
    act(
      () => {
        if (visitId) {
          const built = buildVisitUpdatePayload(draft, range);
          if (built.error) throw new Error(built.error);
          return TspApiService.updateCityVisit(visitId, built.payload);
        }
        return TspApiService.createCityVisit(visitBody({ ...draft, remarks: draft.remarks || "" }, journeyId, locationId, range));
      },
      onVisitChanged,
      "Could not save the visit."
    ).then((result) => (result.ok || !result.message ? result : { ok: false, message: result.message }));

  const ids = (resource) => resource.data || [];
  // "Loading…" only the first time: a list being re-read after a save keeps showing its records.

  return {
    staged: false,
    hasVisit: Boolean(visitId),
    visit: visitRecord ? { id: visitRecord.id, ...visitToDraft(visitRecord) } : null,
    saveVisit,
    // A recorded visit is kept (it is the place's history); it is edited, not removed.
    removeVisit: null,

    books: ids(books).map((row) => ({
      id: row.id,
      bookType: row.book_name || row.custom_book_name || "Book",
      book_set_id: row.book_set_id || null,
      custom_book_name: row.custom_book_name || null,
      language: row.language,
      qty: row.quantity,
      method: row.distribution_method,
    })),
    booksLoading: books.loading && !books.data,
    booksError: books.error,
    reloadBooks: books.reload,
    addBook: (row) =>
      act(async () => TspApiService.createBookDistributionBatch(String(journeyId), bookBatchBody([row], await ensureVisit())), afterVisitSave(books.reload), "Could not save the book distribution."),
    updateBook: (id, row) =>
      act(
        () =>
          TspApiService.updateBookDistribution(id, {
            ...(row.book_set_id ? { book_set_id: row.book_set_id } : { custom_book_name: row.custom_book_name }),
            language: row.language,
            quantity: row.qty,
            distribution_method: row.method,
          }),
        books.reload,
        "Could not update the book distribution."
      ),
    removeBook: (id) => act(() => TspApiService.deleteBookDistribution(id), books.reload, "Could not remove the book distribution."),

    stays: ids(stays).map((row) => ({ id: row.id, stay_name: row.stay_name, contact: row.contact, address: row.address, charges: row.charges, capacity: row.capacity, remarks: row.remarks })),
    staysLoading: stays.loading && !stays.data,
    staysError: stays.error,
    reloadStays: stays.reload,
    addStay: (draft) => act(() => TspApiService.accommodations.create(accommodationBody(draft, journeyId, locationId)), stays.reload, "Could not save the accommodation."),
    updateStay: (id, draft) => act(() => TspApiService.accommodations.update(id, stayChanges(draft)), stays.reload, "Could not update the accommodation."),
    removeStay: (id) => act(() => TspApiService.accommodations.remove(id), stays.reload, "Could not remove the accommodation."),

    media: ids(media).map((item) => ({ id: item.id, caption: item.caption, file_url: item.file_url, media_type: item.media_type, canDelete: item.can_delete === true })),
    mediaLoading: media.loading && !media.data,
    mediaError: media.error,
    reloadMedia: media.reload,
    addMedia: ({ file, caption }) => act(async () => uploadMedia({ caption, journeyId, cityVisitId: await ensureVisit() }, file), afterVisitSave(media.reload), "Could not upload the file."),
    removeMedia: (id) => act(() => deleteMedia(id), media.reload, "Could not remove the file."),

    advice: ids(advice).map((entry) => ({ id: entry.id, sections: entry.sections || {}, canEdit: entry.capabilities ? entry.capabilities.edit !== false : true })),
    adviceLoading: advice.loading && !advice.data,
    adviceError: advice.error,
    reloadAdvice: advice.reload,
    addAdvice: (sections) => act(() => createAdvice(adviceBody(sections, journeyId, locationId)), advice.reload, "Could not save the advice."),
    updateAdvice: (id, sections) => act(() => saveAdviceChanges(id, buildUpdateBody(sectionValues(sections))), advice.reload, "Could not update the advice."),
    removeAdvice: (id) => act(() => deleteAdvice(id), advice.reload, "Could not remove the advice."),

    responses: ids(responses).filter((row) => !visitId || !row.city_visit_id || Number(row.city_visit_id) === Number(visitId)).map((row) => ({ id: row.id, rating: row.response_rating, remarks: row.remarks || "" })),
    responsesLoading: responses.loading && !responses.data,
    responsesError: responses.error,
    reloadResponses: responses.reload,
    addResponse: (response) =>
      act(
        async () => TspApiService.createPublicResponse(responseBody({ rating: response.rating, remarks: response.remarks || "" }, journeyId, await ensureVisit())),
        afterVisitSave(responses.reload),
        "Could not save the public response."
      ),
    updateResponse: (id, response) => act(() => TspApiService.updatePublicResponse(id, { response_rating: response.rating, remarks: response.remarks || null }), responses.reload, "Could not update the public response."),
    removeResponse: (id) => act(() => TspApiService.deletePublicResponse(id), responses.reload, "Could not remove the public response."),
  };
}

// An edited stay -> the update request: every field, an emptied one cleared.
function stayChanges(draft) {
  const textOrNull = (value) => (String(value ?? "").trim() ? String(value).trim() : null);
  return {
    stay_name: String(draft.stay_name).trim(),
    contact: textOrNull(draft.contact),
    address: textOrNull(draft.address),
    remarks: textOrNull(draft.remarks),
    charges: String(draft.charges ?? "").trim() ? Number(draft.charges) : null,
    capacity: String(draft.capacity ?? "").trim() ? Number(draft.capacity) : null,
  };
}

const sectionValues = (sections) => Object.fromEntries(ADVICE_SECTIONS.map((section) => [section.key, sections[section.key] || ""]));
